import { useCallback, useEffect, useRef, useState } from 'react'

import { instantOf } from './freshness.js'
import { TARGET_STOPPED, TARGET_WATCHED } from './watchRequests.js'

// The watch-together projection, as one screen sees it. W2b (P6-244).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND THE PAGE MUST RENDER WITHOUT IT
// ═══════════════════════════════════════════════════════════════════════════
//
// `movie.js`'s rule and `music.js`'s rule, both apply here unchanged: offline,
// 401, 500, 502, 503, a body that is not JSON, no Functions at all under
// `vite dev` — every one of them resolves to a state that renders a sentence
// in the panel and nothing else. No caller needs a `catch`, and no caller can
// forget one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 BUT THEY MUST NOT ALL RESOLVE TO THE SAME SENTENCE — P6-200, APPLIED HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// Music's panel said "Music is unavailable right now" both when the service
// was genuinely off and when the site was reading the wrong bucket, and that
// hid a production outage for as long as it existed. Watch has no cross-bucket
// risk the way music did (see `functions/_lib/projections.js`'s header on
// `readWatchProjection` — it is the bot's own `PROJECTIONS` binding, the same
// one `/api/movie` already uses, not a second credential family), but the
// GENERAL rule still applies: a permanent misconfiguration must not wear the
// same sentence as an ordinary "come back later."
//
// So this file keeps `music.js`'s two-state split rather than inventing a
// third: `MISCONFIGURED` for a 500 (ours, permanent, fixed by a deploy — never
// published, never a binding, never a bucket the site can read) and
// `UNAVAILABLE` for everything else that is not `response.ok` (502 R2 had a
// blip, or 503 the bot has never published — both genuinely read as "right
// now" to a member, and the ONE thing that tells them apart, the reason
// string, is for the operator's log in `functions/api/watch.js`, never for the
// page). `functions/_lib/projections.js::WATCH_FAILURE` already keeps these
// five reasons distinct server-side and logs which one fired; collapsing four
// of them into one member-facing sentence is not losing the distinction, it is
// the same choice `movie.js`'s header states outright: "the states a SCREEN
// has are fewer than the states a Function has."
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THREE FETCH STATES, AND `loading` IS NOT `unavailable`
// ═══════════════════════════════════════════════════════════════════════════
//
// For the first moment of every load NOTHING HAS FAILED. A panel that said
// "watch together is unavailable" there would be the portal reporting an
// outage that has not happened.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 AND A FOURTH THING THAT IS NOT A FETCH FAILURE AT ALL: THE EMPTY LIST
// ═══════════════════════════════════════════════════════════════════════════
//
// `subscriptions: []` is a 200. It is the first thing a brand-new member sees
// — nobody has run `/watch add` yet, or a young server genuinely has nothing
// on the list — and it is drawn as its own designed state in `WatchPanel.jsx`
// (Handoff state 16), never folded into `UNAVAILABLE`. `buildWatch` below does
// not special-case it at all: an empty `subscriptions` array is just a
// perfectly ordinary `READY` payload, which is what makes it impossible to
// confuse with a failure by construction rather than by a branch remembering
// to check for one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 `you` IS NEVER ON THE WIRE — DERIVE IT, NEVER DEFAULT IT
// ═══════════════════════════════════════════════════════════════════════════
//
// `utils/watch_projection.py`'s own docstring (risk item 1) and W2a's report
// (§3) are both explicit: `3a/watch.json` is read by every signed-in member,
// so a per-viewer `you` field on it would publish every viewer's state to
// every other viewer — the exact failure ADR-0003 D5 exists to prevent. There
// is no `3b/<user id>/watch.json` either (the whole list is shared by
// construction), so there is nothing this module could fetch a second time to
// find it.
//
// `deriveYou` below is the join: match the signed-in member's id against
// `members[]` in the SAME payload. Signed out is `{ signedIn: false }` and the
// caller must render no `YOU` chip at all — the other two axes (SHOW, SERVER)
// still render unconditionally, because they are facts about the show and the
// server's shelf, not about the reader.

export const WATCH_ENDPOINT = '/api/watch'

/**
 * Where "Open Discord" / "Open #watch ↗" goes — `WatchPanel.jsx`'s own
 * finding, shared with the title page's "we're here" control (W3b, P6-244).
 *
 * `3a/watch.json` publishes no `guild_id` and no `channel_id` anywhere a
 * client can read, and WT2's own ruling ("posts go to the channel the
 * subscription was created in, no announcement-channel setting") means there
 * is no single "#watch channel" to name even in principle — a title added
 * from one channel and a title added from another have two different "the
 * channel"s. This opens Discord itself (a real, always-valid URL) rather
 * than a fabricated guild or channel link; the member is left to navigate
 * from there. See `docs/reports/W2b.md` for the full finding.
 */
export const DISCORD_HOME = 'https://discord.com/channels/@me'

export const LOADING = 'loading'
export const READY = 'ready'
export const UNAVAILABLE = 'unavailable'

/** The deployment is wrong, and waiting will not fix it. See the header. */
export const MISCONFIGURED = 'misconfigured'

/** The status that means *this deployment is misconfigured*, and nothing
 * else. A constant so the panel, this module and the test cannot disagree —
 * `music.js::DEPLOYMENT_FAULT_STATUS`'s own reasoning, unchanged. */
export const DEPLOYMENT_FAULT_STATUS = 500

/** Nobody has answered yet. Renders nothing, because nothing has failed. */
export function loadingWatch() {
  return { state: LOADING, shared: null }
}

/** It did not load. Renders one sentence, and the rest of the page is intact. */
export function unavailableWatch() {
  return { state: UNAVAILABLE, shared: null }
}

/** It cannot load on this deployment until somebody changes the deployment.
 * Renders a different sentence, for the reason in the header. */
export function misconfiguredWatch() {
  return { state: MISCONFIGURED, shared: null }
}

/**
 * The Function's answer, checked at the seam.
 *
 * ⚠️ THE SHAPE IS CHECKED HERE TOO, and it is not a duplicate of the
 * Function's check. `functions/_lib/projections.js` asserts what came out of
 * the BUCKET; this asserts what came over the WIRE, and those are two
 * different edges with a proxy, a service worker and a browser cache in
 * between.
 */
export function buildWatch(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return unavailableWatch()
  const shared = payload.shared
  if (!shared || typeof shared !== 'object' || Array.isArray(shared)) return unavailableWatch()
  return { state: READY, shared }
}

/**
 * Ask the Function. Resolves to a watch state, always — see the header.
 *
 * ⚠️ `credentials: 'same-origin'`: the session cookie is what makes this a 200
 * rather than a 401. `3a/` is readable by any signed-in member and by nobody
 * else.
 */
export async function fetchWatch(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableWatch()
  try {
    const response = await fetchImpl(WATCH_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      return response.status === DEPLOYMENT_FAULT_STATUS ? misconfiguredWatch() : unavailableWatch()
    }
    return buildWatch(await response.json())
  } catch {
    return unavailableWatch()
  }
}

/**
 * The watch projection for one screen. Starts `loading`, lands `ready` or a
 * failure state.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 A SINGLE FETCH ON MOUNT, NOT A POLL — `movie.js::useMovie`'s SHAPE, NOT
 *    `music.js::useMusic`'s
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * W2a's own report (§8) is explicit about the cadence this file is allowed to
 * assume: "`3a/watch.json` publishes on the same cadence and mechanism as
 * `3a/movie.json` (fingerprint-gated, hourly-floor, digest-skipped) — no new
 * polling or caching logic needed beyond what W2b's movie-panel siblings
 * already do for the movie tab." Music polls every two seconds because a
 * track can change while somebody is looking at the panel (MI7); nothing in
 * this projection moves on that timescale, so a poll here would ask the
 * Function sixty times more often than the data underneath it can ever
 * change.
 *
 * ⚠️ CALLED FROM INSIDE `WatchPanel`, NOT FROM `PortalHome` — unlike
 * `useMovie`, which IS called from `PortalHome` because movie's projection has
 * three other consumers on that page (the identity subline, the activity
 * feed, the rotation strip). Watch has exactly one consumer, the panel itself,
 * so fetching it at page level would ask for the whole server's watchlist on
 * every portal load whether or not anybody ever opens the Watch pill — the
 * same reasoning `music.js` states for why `useMusic` lives inside
 * `MusicPanel` and not beside `useMovie`.
 *
 * ⚠️ ONE REQUEST PER PANEL OPEN AND NO MODULE-LEVEL CACHE — the same trade
 * `movie.js`, `music.js`, `session.js` and `directory.js` all made, for the
 * reason they wrote down: a promise cache saves a request and buys shared
 * mutable state every test has to reset.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `refresh` — V2-3b's own addition, still not a poll
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ADR-0009 D3: "the bot publishes the projection right after" applying a
 * portal request. `refresh()` is the on-demand escape hatch a button uses
 * to re-read `3a/watch.json` once its own request has landed — one extra
 * fetch, member-initiated, never a second automatic timer. It does not
 * reset `state` to `LOADING` first (unlike the mount effect below): a
 * button-triggered refresh must not flash "Loading the watchlist…" over a
 * panel that was already rendering perfectly well a moment ago.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `confirm` — P6-281 §1.1's own addition: RE-READ UNTIL THE CHANGE SHOWS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The applier now publishes the projection BEFORE it reports "applied"
 * (`cogs/watch/requests.py::tick`), so the ordinary case needs no more than
 * the one fetch `refresh()` already makes. `confirm` is the DEFENSE for
 * whatever residual propagation lag remains beyond that fix (an edge
 * cache, R2 replication) — it re-fetches, checks the caller's own
 * `isConfirmed(shared)` against what came back, and retries a bounded
 * number of times before giving up. It always lands the panel on the LAST
 * answer it fetched, confirmed or not, and says which via the resolved
 * boolean — so a caller can render an honest "may take a moment to show
 * up here" instead of silently presenting an unconfirmed read as settled.
 */
/**
 * 🔴 ROUND 2 (V2-2b, manager review): `enabled`, defaulted `true` — a purely
 * additive parameter. `WatchPanel.jsx`'s own call site (`useWatch()`, no
 * argument) is byte-for-byte unaffected; it was already safe to fetch
 * unconditionally there because it only ever mounts signed in (inside
 * `PortalHome`, itself gated by `Landing.jsx` on `SIGNED_IN`). The archive
 * page is a STANDALONE route reachable while signed out, so it is the first
 * caller that needs `false` — Trips.jsx's own pattern, reused: "nothing is
 * asked for until the session says there is somebody to ask for." This is
 * what lets the archive borrow `/api/watch`'s own `members[].progress` for
 * a listed title's status line (`WatchArchive.jsx`) without ever fetching
 * it while signed out.
 */
export function useWatch(enabled = true) {
  const [watch, setWatch] = useState(loadingWatch)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refresh = useCallback(() => {
    // No `.catch`: `fetchWatch` has no rejecting branch, and a handler that
    // can never run reads as if it could.
    fetchWatch().then((answer) => {
      if (mounted.current) setWatch(answer)
    })
  }, [])

  const confirm = useCallback(async (isConfirmed) => {
    for (let attempt = 0; attempt < CONFIRM_TRIES; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop -- each attempt must see
      // the PREVIOUS one's answer before deciding whether to retry.
      const answer = await fetchWatch()
      if (!mounted.current) return false
      setWatch(answer)
      if (answer.state === READY && isConfirmed(answer.shared)) return true
      if (attempt < CONFIRM_TRIES - 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(CONFIRM_INTERVAL_MS)
        if (!mounted.current) return false
      }
    }
    return false
  }, [])

  useEffect(() => {
    if (!enabled) return
    refresh()
  }, [refresh, enabled])

  return { ...watch, refresh, confirm }
}

/** Bounded retry parameters for `confirm`, above — kept small: the ordinary
 * case is already confirmed on the FIRST re-fetch (the server-side fix is
 * the primary defense; this is only for what that fix does not reach), and
 * a button that stays in its "Asking…"-adjacent state for many seconds
 * reads as broken long before this bound would ever be reached.
 *
 * V2-2c: exported — `watchArchive.js::useWatchArchive` gained its own
 * `confirm` (the favourite-reorder buttons need to confirm against a
 * freshly-fetched ARCHIVE, not `/api/watch`, since a favourite can live on
 * a title off the watchlist) and reuses this exact policy rather than
 * inventing a second "how many tries, how far apart" pair that could
 * silently drift from this one — CLAUDE.md gotcha 1's *copies* axis,
 * applied to two retry policies instead of two datastores.
 */
export const CONFIRM_TRIES = 5
export const CONFIRM_INTERVAL_MS = 600

export function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// READING THE BLOB — one place, so the panel and its tests cannot disagree
// ═══════════════════════════════════════════════════════════════════════════

/** Every subscription the blob carries, defensively. `[]` for anything else. */
export function subscriptionsOf(shared) {
  const list = shared?.subscriptions
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/** The guide's raw entries — `{subscription_id, season, episode, airs_at}`,
 * already bounded to the Eastern calendar week and sorted soonest-first by
 * `utils/watch_projection.py::_airing_this_week`. */
export function airingThisWeekOf(shared) {
  const list = shared?.airing_this_week
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

export function subscriptionsByStatus(shared, status) {
  return subscriptionsOf(shared).filter((sub) => sub.status === status)
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IDS — `added_by` AND `members[].id` ARE NOT STRINGS ON THE WIRE TODAY,
//    AND THIS FILE MUST NOT COERCE THEM. FINDING FOR THE REPORT.
// ═══════════════════════════════════════════════════════════════════════════
//
// `music.js` documents the failure shape at length: a Discord snowflake is
// ~10^17–10^18, `Number.MAX_SAFE_INTEGER` is ~9×10^15, so a snowflake
// published as a JSON NUMBER is rounded by `JSON.parse` twice over (once in
// the Worker, once in the browser) and the original digits are not
// recoverable at either end. Music's `requester_id` / `inducted_by` had
// exactly this bug until #426 added `str(...)` on the publisher side.
//
// `utils/watch_projection.py::_subscription_view` publishes `added_by` and
// `members[].id` as direct passthroughs of the store's own Python ints —
// `record.get("added_by")` and `[dict(w) for w in watchers]` — with no
// `str(...)` anywhere on either field. That is `utils/**`, on this slice's DO
// NOT TOUCH list, so the fix belongs upstream (a one-line change mirroring
// #426, per the finding in `docs/reports/W2b.md`). Until it lands, this file
// treats both fields exactly as `music.js::joinableId` treats a music id:
// REFUSED rather than coerced. `String(1039191320375402500)` is a
// well-formed id belonging to nobody, or to the wrong person, and looking a
// rounded id up in the member directory does not miss safely — it misses, or
// it lands on somebody else and attributes a pick or a "who's in" chip to the
// wrong member.
//
// ✅ THE COMPROMISE COSTS NOTHING TO RETIRE. The moment the publisher
// stringifies these two fields, `joinableId` starts accepting them and every
// chip that reads `added_by` or a member id starts naming people — with no
// edit needed here, exactly as `music.js`'s own history records for its
// identical bug.

/** A Discord id as a key segment may contain it — decimal digits, nothing
 * else. Same shape `music.js::SNOWFLAKE` uses, for the same reason. */
const SNOWFLAKE = /^[0-9]{1,32}$/

/** An id this page may look up in the member directory, or `null`. A number
 * is refused rather than `String()`-coerced — see the header above. */
export function joinableId(value) {
  return typeof value === 'string' && SNOWFLAKE.test(value) ? value : null
}

/** The member who added this title, or `null` when we cannot say. */
export function addedById(subscription) {
  return joinableId(subscription?.added_by)
}

/**
 * V2-2c: the member-profile route for one id, or `null` when `id` is not
 * something this page can safely look up (`joinableId`'s own gate — a
 * rounded JSON number, or anything else that fails the snowflake shape,
 * has nothing honest to link to). Every caller that wraps a member chip or
 * avatar (`Person.jsx::MemberLink`) computes `to` through this function
 * rather than building the path literally, so the ROUTE STRING itself has
 * exactly one spelling in the whole site (CLAUDE.md gotcha 1's *copies*
 * axis) — `App.jsx`'s own `NESTED` entry for this route is built from the
 * same literal segment, `/watch/member/`.
 */
export function memberProfilePath(id) {
  return joinableId(id) ? `/watch/member/${id}` : null
}

/** `members[]`, with only the ids this page can safely look up — still
 * carrying `status`, and still counted even when the id had to be dropped
 * (see `membersWereDropped`). */
function membersOf(subscription) {
  const list = subscription?.members
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/** Did this subscription carry a member id the page had to drop? Mirrors
 * `music.js::idsWereDropped` exactly: it asks whether a PRESENT id was
 * unusable, not whether the list is empty — a title nobody has marked into
 * yet has a genuinely empty `members[]`, and that is a measured emptiness,
 * not a dropped id. Drives one honest sentence instead of a row of "someone
 * in the crew" chips that would look like a member-directory outage rather
 * than the real cause. */
export function membersWereDropped(subscription) {
  return membersOf(subscription).some((member) => {
    const raw = member.id
    return raw !== null && raw !== undefined && joinableId(raw) === null
  })
}

export function membersWithStatus(subscription, status) {
  return membersOf(subscription)
    .filter((member) => member.status === status)
    .map((member) => joinableId(member.id))
    .filter((id) => id !== null)
}

/**
 * The signed-in member's own place on this title, or the signed-out answer.
 *
 * Returns `{ signedIn: false, status: null, standing: null }` when nobody is
 * signed in — render no `YOU` chip at all in that case, per the brief's
 * explicit rule. Signed in and not on the list is
 * `{ signedIn: true, status: null, standing: null }`, which is the dashed
 * `YOU NOT MARKED IN` chip. Signed in and on the list is
 * `{ signedIn: true, status: 'watching' | 'stopped' | 'watched', standing }`
 * — P6-282 added `stopped` (quit without finishing) alongside the original
 * two; this function itself needed no change for that, since `status`
 * publishes whatever the wire says verbatim (`utils.watch_manager.
 * MEMBER_STATUSES`, bot-side).
 *
 * P6-299b: `standing` joins `status`, read verbatim off the SAME member
 * entry (`utils/watch_projection.py::_member_standing`, bot-side) — never
 * computed here (brief §2.2: "Never compute it on the site"). `null` for
 * every case `status` is already `null` for (signed out, or on the list
 * with no member entry at all), since the bot has nothing to derive a
 * standing FROM either. See `StandingStrip`'s own `you-chip` branch
 * (`WatchPanel.jsx`) for the one place this is read today.
 */
export function deriveYou(subscription, userId) {
  if (typeof userId !== 'string' || userId === '') return { signedIn: false, status: null, standing: null }
  const match = membersOf(subscription).find((member) => joinableId(member.id) === userId)
  return { signedIn: true, status: match ? match.status : null, standing: match ? (match.standing ?? null) : null }
}

/**
 * The signed-in member's OWN position on this title, or `null` -- P6-281:
 * `deriveYou`'s sibling for the per-member `progress` field
 * `utils/watch_projection.py::_subscription_view` now publishes on each
 * `members[]` entry, alongside `status`. `null` both when signed out and
 * when signed in but this member has never marked a position -- the
 * caller (`nextEpisodeToOffer` / `catchUpTarget`) treats both the same
 * way: nothing of yours to advance FROM.
 *
 * ⚠️ NEVER `subscription.progress` for THIS question. That field is now
 * the GROUP's own DERIVED position (`utils/watch_manager.py::group_progress`)
 * -- correct for `toWatchInfo`/`whereWeAreCaption`/the card, which still
 * read it unchanged, and wrong for "what should +1 propose", which is
 * this function's whole job.
 */
export function deriveYourProgress(subscription, userId) {
  if (typeof userId !== 'string' || userId === '') return null
  const match = membersOf(subscription).find((member) => joinableId(member.id) === userId)
  const progress = match?.progress
  return progress && Number.isInteger(progress.episode)
    ? { season: progress.season ?? null, episode: progress.episode }
    : null
}

/** Does this member's review carry real text? `rating` alone (no review
 * written) is not a review row. Moved here in V2-4c from `WatchTitle.jsx`
 * (unchanged) so `ActionBar.jsx`'s own Rate it / Review it button can ask
 * the identical question when deciding between "Rate it," "Review it" and
 * "Rated N/10 · change" -- CLAUDE.md gotcha 1's *copies* axis: two
 * hand-typed versions of "does this look like a review" are two chances to
 * disagree about an empty string or a stray whitespace-only value. */
export function hasReviewText(member) {
  return (
    member &&
    typeof member === 'object' &&
    member.review &&
    typeof member.review === 'object' &&
    typeof member.review.text === 'string' &&
    member.review.text.trim() !== ''
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// KIND AND SOURCE LABELS — shared by the card's meta line and the title
// page's hero meta line (W3b, P6-244)
// ═══════════════════════════════════════════════════════════════════════════
//
// Lifted out of `WatchPanel.jsx::MetaLine` rather than re-typed a second time
// in `WatchTitle.jsx` — CLAUDE.md gotcha 1's *copies* axis: two hand-written
// label maps are two chances for "ANIME" to become "Anime" in one of them.

const SHOW_KIND_LABEL = Object.freeze({ tv: 'TV', anime: 'ANIME', movie: 'FILM' })
const SOURCE_LABEL = Object.freeze({ tv: 'TMDB', movie: 'TMDB', anime: 'ANILIST' })

/** `TV` / `ANIME` / `FILM`, or the raw kind uppercased for anything this
 * store has not shipped yet — never a blank cell. */
export function kindLabel(kind) {
  return SHOW_KIND_LABEL[kind] ?? kind?.toUpperCase() ?? '—'
}

/** `TMDB` / `ANILIST`, or `—` for a kind with no known source. */
export function sourceLabel(kind) {
  return SOURCE_LABEL[kind] ?? '—'
}

// ═══════════════════════════════════════════════════════════════════════════
// THE STANDING STRIP — three axes, never conflated (Handoff's organising idea)
// ═══════════════════════════════════════════════════════════════════════════

const SHOW_LABEL = Object.freeze({
  airing: 'AIRING',
  between_seasons: 'BETWEEN SEASONS',
  ended: 'ENDED',
  cancelled: 'CANCELLED',
  not_checked_yet: 'NOT CHECKED YET',
  released: 'RELEASED',
})

/**
 * The `SHOW` chip's value and label.
 *
 * Built from `series_status` (four published values + `null`) plus `next_up`
 * plus `kind`, exactly as Handoff §State's "Derived on the client" list and
 * `utils/watch_projection.py`'s own docstring (item 2) both specify — the
 * server deliberately stops at four raw values and leaves "between seasons"
 * and "released" to this join.
 *
 * ⚠️ ORDER MATTERS. `null` (never checked) is checked first so a just-added
 * title never falls into "released" or "between seasons" by accident; the
 * film check is checked before the generic airing-with-no-next-up check so a
 * released film reads `RELEASED` rather than `BETWEEN SEASONS` (TMDB's
 * conservative default already makes a film's `series_status` read
 * `"airing"`, which Handoff finding 5 says not to trust for a film at all).
 */
export function showStanding(subscription) {
  const status = subscription?.series_status ?? null
  if (status === null) return { value: 'not_checked_yet', label: SHOW_LABEL.not_checked_yet }
  if (subscription?.kind === 'movie' && subscription?.next_up === null && subscription?.release_date) {
    return { value: 'released', label: SHOW_LABEL.released }
  }
  if (status === 'between_seasons' || (status === 'airing' && subscription?.next_up === null)) {
    return { value: 'between_seasons', label: SHOW_LABEL.between_seasons }
  }
  return { value: status, label: SHOW_LABEL[status] ?? status.toUpperCase() }
}

const SERVER_LABEL = Object.freeze({
  watching: 'WATCHING',
  // P6-299b: `awaiting` joins the three raw `status` values below — a shelf
  // value `status` itself never carries (see `titleShelf`'s own docstring,
  // further down this file).
  awaiting: 'AWAITING',
  finished: 'FINISHED',
  dropped: 'DROPPED',
})

/**
 * The `SERVER` chip's value and label.
 *
 * 🔴 P6-299b: reads the published `shelf` (`titleShelf`, below), NOT the raw
 * `status` this used to read directly. Brief §2.1: "the chip can say WATCHING
 * on a title sitting under Finished [once the shelf follows the crew] — make
 * the chip read `shelf`... so a title's chip always matches the tab it sits
 * in." Gotcha 21's own shape: this chip and `watchListTab` (below) are two
 * renderings of the SAME fact, and must read the SAME field or they can
 * silently disagree the moment a title's shelf and its raw `status` diverge
 * (P6-299a's own acceptance table has three such cases). A missing/
 * unrecognised shelf reads as `watching`, identically to every other reader
 * of `titleShelf` — never a bespoke `null` answer for this one caller.
 *
 * ⚠️ NO DATE, UNCHANGED. Handoff draws `SERVER FINISHED 6 SEP` and
 * `DROPPED 4 MAR`; the store records only the CURRENT `status` on a
 * subscription record (W0's own shape), never the instant it last changed —
 * no `finished_at`, no `dropped_at`, nothing in `news[]` either (that feed is
 * the nightly diff's, keyed to the SHOW's own status, not to a member running
 * `/watch finish`). There is no honest date to render, whether the label is
 * built from `status` or from `shelf` — see `docs/reports/W2b.md` for this as
 * a finding rather than a silent gap.
 */
export function serverStanding(subscription) {
  const value = titleShelf(subscription)
  return { value, label: SERVER_LABEL[value] ?? value.toUpperCase() }
}

// ═══════════════════════════════════════════════════════════════════════════
// THE ARITHMETIC — a correctness constraint, not a style one (Handoff)
// ═══════════════════════════════════════════════════════════════════════════

/** `S4E7`, or `E9` when there is no season number (an AniList title — Handoff
 * state 3's `NO SEASON NUMBERS ON ANILIST`). `null` in, `null` out. */
export function episodeCode(season, episode) {
  if (!Number.isInteger(episode)) return null
  return Number.isInteger(season) ? `S${season}E${episode}` : `E${episode}`
}

/**
 * A `threads` object key -> `S4E7` / `E9`, or `null`.
 *
 * `cogs/watch/refresh.py::_thread_key` writes `f"{season}x{episode}"` when
 * there is a season and the bare episode number (`str(episode)`) when there
 * is not — this is the ONE place that format is read back, so the parser
 * lives beside `episodeCode` rather than being re-derived at each call site.
 * A key this cannot parse (the store's shape changed, or a hand-edited file)
 * returns `null` rather than rendering a raw key like `"3x5"` verbatim.
 */
export function episodeCodeFromThreadKey(key) {
  if (typeof key !== 'string') return null
  if (key.includes('x')) {
    const [seasonPart, episodePart] = key.split('x')
    const season = Number(seasonPart)
    const episode = Number(episodePart)
    return Number.isInteger(season) && Number.isInteger(episode) ? episodeCode(season, episode) : null
  }
  const episode = Number(key)
  return Number.isInteger(episode) ? episodeCode(null, episode) : null
}

/**
 * V2-8b: MOVED HERE FROM `watchTitle.js`, unchanged in behaviour — the
 * watchlist row's own per-season track (`seasonTrackFor`, below) needs the
 * identical comparison the title page's progress lines already use, and
 * the brief's own words are explicit: "use the same position arithmetic
 * the title page's progress lines use. Import it; don't write a second
 * one." `watchTitle.js` already imports FROM this file (`joinableId`,
 * `shortEasternDate`), so importing this back the other way would be a
 * circular import between the two modules — moving the function itself
 * here, once, is what keeps the dependency one-directional. `watchTitle.js`
 * and `WatchTitle.jsx` now import it from here instead (CLAUDE.md gotcha
 * 1's *copies* axis: one function, one home, however many callers).
 *
 * Two `(season, episode)` positions, compared. `null` when they are not
 * comparable at all (one side carries a season number and the other does
 * not) — which should not arise within one subscription's own data (a
 * title is consistently anime-shaped or TV-shaped throughout), but a
 * comparator that silently guessed would be worse than one that says
 * "cannot tell". `-1`/`0`/`1` otherwise, ordinary tuple order.
 */
export function comparePosition(a, b) {
  const aSeason = a?.season ?? null
  const bSeason = b?.season ?? null
  if (aSeason !== bSeason) {
    if (aSeason === null || bSeason === null) return null
    return aSeason < bSeason ? -1 : 1
  }
  const aEpisode = a?.episode
  const bEpisode = b?.episode
  if (!Number.isInteger(aEpisode) || !Number.isInteger(bEpisode)) return null
  if (aEpisode === bEpisode) return 0
  return aEpisode < bEpisode ? -1 : 1
}

/**
 * V2-8b: MOVED HERE FROM `watchTitle.js`, unchanged in behaviour — see
 * `comparePosition`'s own header immediately above for why (the row's own
 * You box needs it for the identical "who is the sole leader" question
 * `yourStanding`, below, already asks on the title page).
 *
 * Who is AT the group's position, as joinable ids. Reads
 * `subscription.progress.leader_ids`, published by
 * `utils/watch_projection.py::_subscription_view` (P6-281) as
 * snowflake-stringified ids, empty when the group's position fell back to
 * the legacy floor (nobody CURRENT set it). `joinableId` is still the one
 * gate — an id that cannot be looked up safely is dropped rather than
 * rendered wrong.
 *
 * A fixture that predates P6-281's `leader_ids` addition (no such key at
 * all) reads as `[]` here, exactly like an empty one.
 */
export function groupLeaderIds(subscription) {
  const ids = subscription?.progress?.leader_ids
  if (!Array.isArray(ids)) return []
  return ids.filter((id) => joinableId(id) !== null)
}

/**
 * Where the group stands against the last aired episode.
 *
 * `to_watch = last_aired.episode − progress.episode + 1`, and ONLY within one
 * season — Handoff's own arithmetic section, worked through against its own
 * example (`Group on S4E5 — 3 to watch` when `last_aired` is S4E7: 7−5+1=3,
 * meaning E5 itself is still unwatched — "progress" names the episode the
 * group is NEXT up to, not the last one finished).
 *
 * Four shapes, never a fifth invented one:
 *   - `none`        no `last_aired` at all yet (nothing has aired that the
 *                    bot has observed) or no `progress` recorded.
 *   - `caught_up`    same season, `progress.episode === last_aired.episode`.
 *                    Handoff: "appears only when the show is airing and the
 *                    group is level with `last_aired`."
 *   - `behind`       same season, progress strictly behind. Carries the
 *                    count.
 *   - `cross_season` `progress.season !== last_aired.season` (including one
 *                    side null and the other not — an anime's `null`/`null`
 *                    pair counts as the SAME season for this comparison, per
 *                    Handoff state 3). NEVER a number — Handoff: "no count
 *                    across a season boundary, ever."
 */
export function toWatchInfo(subscription) {
  const progress = subscription?.progress
  const lastAired = subscription?.last_aired
  const pSeason = progress?.season ?? null
  const pEpisode = progress?.episode
  const lSeason = lastAired?.season ?? null
  const lEpisode = lastAired?.episode

  if (!lastAired || !Number.isInteger(lEpisode) || !progress || !Number.isInteger(pEpisode)) {
    return { mode: 'none' }
  }

  if (pSeason !== lSeason) {
    return {
      mode: 'cross_season',
      progress: episodeCode(pSeason, pEpisode),
      lastAired: episodeCode(lSeason, lEpisode),
      endedOrCancelled:
        subscription?.series_status === 'ended' || subscription?.series_status === 'cancelled',
    }
  }

  if (pEpisode === lEpisode) {
    return { mode: 'caught_up', at: episodeCode(pSeason, pEpisode) }
  }
  if (pEpisode > lEpisode) {
    // A data anomaly (the group cannot be ahead of what has aired). Never
    // invent a negative count — read it the same as level.
    return { mode: 'caught_up', at: episodeCode(pSeason, pEpisode) }
  }
  return {
    mode: 'behind',
    progress: episodeCode(pSeason, pEpisode),
    count: lEpisode - pEpisode + 1,
  }
}

/**
 * The `{season, episode}` the +1 stepper may honestly propose, or `null`
 * when there is none to offer.
 *
 * 🔴 P6-281: reads `yourProgress` (the SIGNED-IN MEMBER'S OWN position --
 * `deriveYourProgress`, above), never `subscription.progress` (the
 * group's now-DERIVED position). "+1 means your position plus one" (the
 * brief, verbatim) -- offering the group's own furthest member's next
 * episode to someone who has not caught up to them yet would let a
 * member skip ahead of episodes they never marked as seen. `yourProgress`
 * is an explicit parameter, never re-derived internally, so a caller
 * that forgets to pass it gets an obvious `undefined` rather than this
 * function silently reaching for the wrong field.
 *
 * 🔴 ITS OWN, EXPLICIT SAFETY GATE — Manager review, 2026-09-23, kept true
 * under P6-281. Before this existed, the stepper was gated on
 * `toWatchInfo(subscription).mode === 'behind'`, which happens to imply
 * the same bound today (`behind` requires `pEpisode < lEpisode`, so
 * `pEpisode + 1 <= lEpisode` always holds) — but that is an ACCIDENT of a
 * helper built for the "where we are" DISPLAY line (and, since P6-281,
 * one that reads the GROUP's position, not yours), not a rule anyone
 * designed or tested as a safety property.
 *
 * This is the site's UI-level half of the identical rule
 * `cogs/watch/requests.py::_target_has_aired` enforces independently,
 * against the manager's own live record. A stale page can only disagree
 * with the bot in the HONEST direction here (offering nothing when the
 * bot would in fact still refuse, or — impossible by construction, since
 * `last_aired` only ever moves forward — never the dangerous one of
 * offering an episode the bot would refuse).
 */
export function nextEpisodeToOffer(subscription, yourProgress) {
  const lastAired = subscription?.last_aired
  if (!yourProgress || !Number.isInteger(yourProgress.episode)) return null
  if (!lastAired || !Number.isInteger(lastAired.episode)) return null
  const season = yourProgress.season ?? null
  if (season !== (lastAired.season ?? null)) return null
  const episode = yourProgress.episode + 1
  if (episode > lastAired.episode) return null
  return { season, episode }
}

/**
 * The one-tap "I've seen up to E<latest aired>" target, or `null` when
 * there is none to offer -- P6-281 brief §1.3: "a show with no position
 * couldn't be set from the site."
 *
 * Only when `yourProgress` is genuinely `null` (you have never marked a
 * position on this title at all) AND the title has a real `last_aired` to
 * offer -- once you have ANY position of your own, `nextEpisodeToOffer`
 * above is the honest next step, never this (this is a CATCH-UP, not a
 * second way to advance one episode at a time). Returns `last_aired`
 * itself, verbatim (season may be `null` — AniList) — the same "offer the
 * bot's own already-aired fact, never `progress + 1`" discipline
 * `watchTitle.js::progressCommand` already documents for the copy-command
 * control this control now sits beside.
 */
export function catchUpTarget(subscription, yourProgress) {
  if (yourProgress !== null && yourProgress !== undefined) return null
  const lastAired = subscription?.last_aired
  if (!lastAired || !Number.isInteger(lastAired.episode)) return null
  return { season: lastAired.season ?? null, episode: lastAired.episode }
}

/**
 * V2-8a, manager review round 2: are you EXACTLY caught up — your own
 * position is the show's own `last_aired`, same season, same episode?
 * `nextEpisodeToOffer` and `catchUpTarget` both decline whenever this is
 * true, but they ALSO decline for reasons that are NOT "caught up" (a
 * season boundary, nothing aired yet at all, no position of your own) —
 * this is the one, narrow, exported predicate for the specific fact
 * `ActionBar` needs: is this the "you're caught up" case, the ONLY one
 * where the read-only copy-the-command control (`progressFallback`) is no
 * longer the honest fallback (manager review round 2 — since P6-283 the
 * episode list already lets a member mark any aired episode directly, so
 * a command that would just tell you to mark yourself at your OWN current
 * position has no job left). Every OTHER "nothing to offer" reason keeps
 * the copy-command control exactly as before.
 */
export function caughtUpAtLastAired(subscription, yourProgress) {
  const lastAired = subscription?.last_aired
  if (!yourProgress || !Number.isInteger(yourProgress.episode)) return false
  if (!lastAired || !Number.isInteger(lastAired.episode)) return false
  if ((yourProgress.season ?? null) !== (lastAired.season ?? null)) return false
  return yourProgress.episode === lastAired.episode
}

/**
 * The design's "blocked" state — you are caught up (`caughtUpAtLastAired`)
 * AND the bot already knows when the next episode airs. The prototype's
 * own `bar()`: `mine >= sh.aired && sh.next` → `blocked`, labelled
 * `"+1 once " + code(mine+1) + " airs · " + sh.next.when`.
 *
 * Returns the next episode's `{season, episode, airsAtMs}`, or `null` —
 * never a guessed date. `null` when you are not caught up at all (a
 * different, still-fallback-eligible case — see `caughtUpAtLastAired`), or
 * when the bot has not published a dated `next_up` (an un-dated "next
 * season" note is not a promise this control can quote — it renders
 * nothing rather than guess).
 */
export function caughtUpNextInfo(subscription, yourProgress) {
  if (!caughtUpAtLastAired(subscription, yourProgress)) return null
  const nextUp = subscription?.next_up
  if (!nextUp || !Number.isInteger(nextUp.episode)) return null
  const airsAtMs = instantOf(nextUp.airs_at)
  if (airsAtMs === null) return null
  return { season: nextUp.season ?? null, episode: nextUp.episode, airsAtMs }
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8a: TITLE HUE — a pure, per-title colour, never a second palette
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF.md's own words: "Per-title colour: every title has a `hue`
// (0–360). If there is none, derive one: `(sum of char codes of id × 37) %
// 360`." Nothing this store publishes today carries an explicit `hue` (no
// poster-derived colour exists yet), so in practice every call derives —
// but the explicit-first shape is kept because a future poster-colour
// extraction (Handoff's own "or extract it from the poster" fallback under
// Assets) only has to start PUBLISHING `hue`, never touch this function.
//
// 🔴 THIS DRIVES A WASH AND A GLOW, NEVER A SECOND PALETTE. The design's own
// tokens (`--ember`, `--dusk`, the ink/paper ramp) are unchanged by this —
// `hueForTitle` only ever feeds an `oklch(L C h)` wash/glow that SURROUNDS a
// poster or a card, per Handoff's colour-roles section. A real poster still
// renders as the poster (`PosterSlot`, unchanged); the hue only tints what
// is AROUND it.
export function hueForTitle(subscription) {
  const explicit = subscription?.hue
  if (typeof explicit === 'number' && Number.isFinite(explicit)) {
    return ((explicit % 360) + 360) % 360
  }
  const id = typeof subscription?.id === 'string' ? subscription.id : ''
  let sum = 0
  for (let i = 0; i < id.length; i += 1) sum += id.charCodeAt(i)
  return (sum * 37) % 360
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8a: "FINAL EPISODE" — the site's half of a contract with the bot
// ═══════════════════════════════════════════════════════════════════════════
//
// The "· finale" label on the action bar's advance button is a CLAIM that
// marking this episode finishes the member — and the bot, not the site, is
// what actually decides that (`utils/watch_manager.py::_is_final_episode`).
// This is the site's own implementation of the SAME predicate, over the
// SAME two facts the bot's own docstring names as the only things it
// positively knows: the title's own `series_status` (already normalised to
// this store's four-value wire vocabulary — `ended` is exactly the bot's
// `SERIES_ENDED_STATUSES`, TERMINAL minus CANCELLED, never the raw provider
// string) and `last_aired` (`{season, episode}` — "the newest episode we
// know exists at all," the same field `catchUpTarget` and `progressCommand`
// already treat as this store's one source of truth for "has this aired").
//
// **Anything this store does not positively know is NOT final** — every
// branch below is a distinct way of refusing to guess, mirroring the bot's
// own docstring table exactly:
//   - a movie has no episode concept at all — unconditionally `false`;
//   - the show has not ENDED (`airing`, `between_seasons`, `cancelled`, or
//     never checked at all — `series_status === null`) — a cancelled show's
//     last aired episode may not be the story's own intended ending, so
//     cancelled is uncertain, never final, exactly like "still airing";
//   - the target is not the show's own recorded LAST episode — no
//     `last_aired` at all (never refreshed) is "we do not know," never
//     final, and never guessed from `episode_count` (a series-wide total,
//     not necessarily what has actually aired).
//
// Season comparison is a plain equality, deliberately — both sides use the
// same `null`-for-AniList convention `last_aired.season` already carries
// (never a comparison-only normalisation): an anime's `null` season and a
// TV show's real season number are never treated as interchangeable.
//
// 🔴 THIS PREDICATE IS PROVEN AGAINST THE BOT'S OWN, THROUGH ONE SHARED
// FIXTURE (`site/src/test/fixtures/final-episode-cases.json`), NOT ASSUMED
// TO AGREE WITH IT — CLAUDE.md gotcha 21: two implementations of one
// contract, checked against a common artifact. See
// `tests/test_watch_final_episode_contract.py` for the bot side.
export function isFinalEpisode(subscription, season, episode) {
  if (subscription?.kind === 'movie') return false
  if (subscription?.series_status !== 'ended') return false
  const lastAired = subscription?.last_aired
  if (!lastAired || !Number.isInteger(lastAired.episode)) return false
  return (lastAired.season ?? null) === (season ?? null) && lastAired.episode === episode
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8b: "YOUR STANDING" — the row's own You box wording, MOVED HERE FROM
// `WatchTitle.jsx` (unchanged in behaviour). The brief's own words: "your
// You box... with V2-8a's standing wording." It was private to the title
// page's own component file; the row needs the IDENTICAL six wordings
// (ahead / level / behind by N / no position yet / finished / stopped, plus
// a movie's watched/not-watched pair), so it moves to this shared module
// rather than being re-typed a second time — CLAUDE.md gotcha 1's *copies*
// axis, the same reasoning `hueForTitle`/`isFinalEpisode` above already
// followed in V2-8a. `WatchTitle.jsx` now imports it from here; its own
// call site is unchanged.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Your own standing, in the six words the brief asks this slice to
 * decide: ahead, level, behind by N, no position yet, finished, stopped —
 * P6-281's per-member positions, compared against the group's own derived
 * furthest (`subscription.progress`), never against `last_aired` directly
 * (that comparison is `toWatchInfo`'s job, for the GROUP card).
 *
 * `null` when signed out — the caller renders no "You" card at all, the
 * same `showYou = signed` rule the pre-v2 standing strip already used.
 */
export function yourStanding(subscription, yourProgress, you, userId) {
  if (!you.signedIn) return null
  if (subscription?.kind === 'movie') {
    return { value: you.status === TARGET_WATCHED ? 'Watched ✓' : 'Not watched yet', note: null, tone: null }
  }
  if (you.status === TARGET_WATCHED) {
    return { value: 'Finished ✓', note: null, tone: null }
  }
  if (you.status === TARGET_STOPPED) {
    const code = Number.isInteger(yourProgress?.episode) ? episodeCode(yourProgress.season, yourProgress.episode) : null
    return { value: code ? `Stopped at ${code}` : 'Stopped', note: 'Not counted in the group', tone: 'quiet' }
  }
  if (!Number.isInteger(yourProgress?.episode)) {
    return { value: 'No position yet', note: 'Mark one below', tone: 'quiet' }
  }

  const code = episodeCode(yourProgress.season, yourProgress.episode)
  const lastAired = subscription?.last_aired
  const atLastAired =
    Number.isInteger(lastAired?.episode) &&
    comparePosition(yourProgress, { season: lastAired.season ?? null, episode: lastAired.episode }) === 0
  const groupProgress = subscription?.progress
  const cmp = Number.isInteger(groupProgress?.episode)
    ? comparePosition(yourProgress, { season: groupProgress.season ?? null, episode: groupProgress.episode })
    : null

  if (cmp !== null && cmp < 0) {
    const sameSeason = (yourProgress.season ?? null) === (groupProgress.season ?? null)
    const behind = sameSeason ? groupProgress.episode - yourProgress.episode : null
    return {
      value: code,
      note: Number.isInteger(behind) ? `You're ${behind} behind` : "Behind the group",
      tone: 'behind',
    }
  }

  const leaderIds = groupLeaderIds(subscription)
  const youAreSoleLeader = leaderIds.length === 1 && leaderIds[0] === userId
  if (youAreSoleLeader) {
    return { value: code, note: atLastAired ? 'Ahead of the group · caught up' : 'Ahead of the group', tone: 'ahead' }
  }
  return { value: code, note: atLastAired ? 'Level with the group · caught up' : 'Level with the group', tone: 'quiet' }
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8b: THE PER-SEASON EPISODE TRACK — the row's own compact season data,
// PLUS the same position arithmetic the title page's progress lines use
// (`comparePosition`, above). Never a second implementation of "which
// season is current" either — `defaultSeasonNumber` below is MOVED HERE
// FROM `WatchTitle.jsx` (unchanged in behaviour), because it never read
// anything title-page-specific in the first place: it only ever needed a
// subscription's own DERIVED group position and a list of `{number}`
// season entries — a shape `season_episode_counts` (the bot's own new
// compact field, `utils/watch_projection.py::_season_episode_counts`)
// satisfies exactly as well as the title page's own fully-detailed
// `seasons[]` does, since neither this function nor its caller ever reads
// an episode's own title or date.
// ═══════════════════════════════════════════════════════════════════════════

/** `subscription.season_episode_counts`, defensively — `[]` for anything
 * else (a movie, or a title whose seasons have not been fetched at all
 * yet — the bot's own two honest reasons to publish nothing here, see
 * `_season_episode_counts`'s own docstring). */
export function seasonEpisodeCountsOf(subscription) {
  const list = subscription?.season_episode_counts
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/**
 * Which season number a row's track should draw — MOVED, UNCHANGED, from
 * `WatchTitle.jsx`'s own `defaultSeasonNumber` (the title page's episode
 * list and progress lines already default to this exact season). Prefers
 * the season the GROUP's own derived position (`subscription.progress`)
 * is in; falls back to the LAST known season when the group has no
 * position yet, or when its season is not among the ones this store has
 * ever seen (a season TMDB has since merged or removed — `_tv_season_
 * numbers`'s own rare case). `undefined` when there is nothing to pick
 * from at all (`seasons` is empty — a movie, or seasons not fetched yet).
 */
export function defaultSeasonNumber(subscription, seasons) {
  if (seasons.length === 0) return undefined
  const progress = subscription?.progress
  if (Number.isInteger(progress?.episode)) {
    const match = seasons.find((s) => (s.number ?? null) === (progress.season ?? null))
    if (match) return match.number ?? null
  }
  return seasons[seasons.length - 1].number ?? null
}

/**
 * The row's own per-season episode track — the design's four cell states,
 * verbatim: dusk for an episode YOU have seen, grey for one the group has
 * seen ahead of you, outlined for aired but unseen (by either), dashed for
 * not yet aired. Built entirely from `comparePosition` — the SAME
 * arithmetic `MemberProgressLine`'s own per-episode variant computation
 * (`WatchTitle.jsx`) uses for the identical four-way split, never a second
 * one keyed on absolute episode numbers.
 *
 * `season` is one entry of `seasonEpisodeCountsOf`'s own return
 * (`{number, episode_count}`); `[]` when its `episode_count` is not a
 * known integer yet (the bot's own honest "not fetched" `null` — never
 * guessed as a full-looking track for a season this store cannot
 * actually say anything about). A zero or negative count (never produced
 * by the bot's own `_season_episode_counts`, but not this function's to
 * assume) reads `[]` too, for free — the loop below simply never runs —
 * so there is deliberately no separate `count <= 0` guard here: mutation-
 * tested, and a redundant one was unkillable by construction, the exact
 * `ActionBar.jsx`/`isMovie` shape V2-8a's own report already found.
 */
export function seasonTrackFor(subscription, season, yourProgress) {
  const seasonNumber = season?.number ?? null
  const count = season?.episode_count
  if (!Number.isInteger(count)) return []

  const lastAired = subscription?.last_aired
  const groupProgress = subscription?.progress
  const cells = []
  for (let episode = 1; episode <= count; episode += 1) {
    const point = { season: seasonNumber, episode }
    const you =
      Number.isInteger(yourProgress?.episode) &&
      comparePosition(point, { season: yourProgress.season ?? null, episode: yourProgress.episode }) <= 0
    const group =
      !you &&
      Number.isInteger(groupProgress?.episode) &&
      comparePosition(point, { season: groupProgress.season ?? null, episode: groupProgress.episode }) <= 0
    const aired =
      Number.isInteger(lastAired?.episode) &&
      comparePosition(point, { season: lastAired.season ?? null, episode: lastAired.episode }) <= 0
    cells.push({ you, group, aired: !you && !group && aired, future: !you && !group && !aired })
  }
  return cells
}

// ═══════════════════════════════════════════════════════════════════════════
// THE "WHERE WE ARE" CAPTION — degraded honestly, not invented
// ═══════════════════════════════════════════════════════════════════════════

/**
 * When the last-aired episode became current, or `null` when this blob
 * cannot say.
 *
 * `last_aired` (`{season, episode, title}`) carries no timestamp of its own.
 * `utils/watch_manager.py`'s own docstring says the field IS "the value the
 * nightly diff's `aired` event fires on" — so the newest `kind: "aired"` entry
 * in this subscription's own `news[]` (already newest-first) is the instant
 * that fired it, joined by STRUCTURE (they are written in the same save, per
 * `cogs/watch/refresh.py`) rather than by parsing the event's free-text
 * `detail` string — which this file must not do (CLAUDE.md gotcha 23's family
 * of "a search that lies by stopping early" is exactly the shape of trusting
 * prose written for a different purpose).
 *
 * `null` when `news[]` has rolled the event off (`MAX_RECENT_EVENTS` is
 * store-wide, not per-title) or never carried one at all — the caption falls
 * back to the episode code alone rather than a guessed date.
 */
export function lastAiredAt(subscription) {
  const news = Array.isArray(subscription?.news) ? subscription.news : []
  const hit = news.find((entry) => entry?.kind === 'aired')
  return hit ? instantOf(hit.at) : null
}

/**
 * The mono caption under "Where we are" — Handoff's worked example is
 * `S3E5 aired 13 Sep · set 14 Sep by @tinny`.
 *
 * ⚠️ NEVER "BY @HANDLE". `progress.updated_by` is not published — see
 * `docs/reports/W2b.md`'s finding — so this renders only what the payload
 * actually carries: the last-aired episode (with its date when `news[]` still
 * has it, per `lastAiredAt`) and when progress was last set. No name is
 * attached because none is available, and inventing one would be exactly the
 * "who set it" fabrication the arithmetic rule already forbids for numbers.
 */
export function whereWeAreCaption(subscription, nowMs) {
  const parts = []
  const lastAired = subscription?.last_aired
  const code = episodeCode(lastAired?.season ?? null, lastAired?.episode)
  if (code) {
    const airedMs = lastAiredAt(subscription)
    parts.push(airedMs === null ? `${code} aired` : `${code} aired ${shortEasternDate(airedMs)}`)
  }
  const updatedAt = instantOf(subscription?.progress?.updated_at)
  if (updatedAt !== null) parts.push(`progress set ${shortEasternDate(updatedAt)}`)
  if (subscription?.kind === 'anime' && (lastAired?.season ?? null) === null && code) {
    parts.push('no season numbers on AniList')
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

// ═══════════════════════════════════════════════════════════════════════════
// FRESHNESS — two DIFFERENT staleness concepts, per the brief's rule 2
// ═══════════════════════════════════════════════════════════════════════════

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/** A single title's own data has gone stale (Handoff state 11): the countdown
 * is hidden because it may no longer be trustworthy. This is NOT the same
 * fact as the whole projection's `as_of` ageing — that is the ordinary
 * freshness stamp every panel already renders (`freshness.js`), and it says
 * nothing about any one title. */
export function isStale(subscription, nowMs) {
  const checked = instantOf(subscription?.last_checked)
  if (checked === null) return false
  return nowMs - checked > 48 * HOUR_MS
}

/** The where-to-watch list may have moved (Handoff state 13's ember `MAY HAVE
 * MOVED`). `providers` itself, never `providers_as_of`, is the authority on
 * whether there IS a list — see `utils/watch_projection.py`'s own note on the
 * asymmetry between the two. This only asks whether the date on an EXISTING
 * list has gone old. */
export function providersOld(subscription, nowMs) {
  const asOf = instantOf(subscription?.providers_as_of)
  if (asOf === null) return false
  return nowMs - asOf > 21 * DAY_MS
}

// ═══════════════════════════════════════════════════════════════════════════
// THE COUNTDOWN — grain by SOURCE, and the grain never upgrades itself
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Is this subscription's countdown an exact instant, or a date-only one?
 *
 * WT4: AniList is the only provider for `kind === 'anime'`; TMDB is the only
 * provider for `tv` and `movie`. AniList gives `nextAiringEpisode` as a real
 * timestamp; TMDB gives a date and the bot assumes an air hour
 * (`watch.assumed_air_hour`, 21:00 ET). So the grain is a property of `kind`
 * alone — there is no third provider in v1 and no field on the wire that
 * could ever disagree with this. Handoff finding 6: the day-grain rule holds
 * even if the assumed hour is ever surfaced, because an assumption that is
 * configurable is still an assumption.
 */
export function isExactCountdown(subscription) {
  return subscription?.kind === 'anime'
}

/** `5d 21h 20m 14s` from whole seconds. Negative input reads as all-zero
 * rather than a negative duration — a stale countdown is caught by
 * `isStale`, not by this formatter inventing a sign. */
export function exactCountdownText(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const d = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (d > 0) return `${d}d ${h}h ${m}m ${s}s`
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** `tonight`, `tomorrow`, `in 2 days` — CALENDAR-day arithmetic in Eastern
 * time, never hour-of-day math. A date-only title's hours are an assumption
 * (`watch.assumed_air_hour`); rendering "tonight" for anything sooner than 24
 * literal hours would leak that assumption back out as if it were knowledge —
 * the exact thing Handoff's honesty rule forbids. */
export function dayGrainCountdownText(airsAtMs, nowMs) {
  const airsKey = easternDateKey(airsAtMs)
  const nowKey = easternDateKey(nowMs)
  const days = easternDaysBetween(nowKey, airsKey)
  if (days < 0) return 'today'
  if (days === 0) return 'tonight'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}

/**
 * The countdown for one subscription's `next_up`, or `null` when there is
 * none to show (no `next_up`, or the title is stale — Handoff state 11: "the
 * countdown is removed, not greyed").
 *
 * `grain: 'exact' | 'day'` tells the caller which rendering to use — a ticking
 * clock for `exact`, a static word for `day`. Only `exact` needs `nowMs` to
 * keep moving; a caller re-deriving this once a second for an exact-grain
 * subscription is how the tick happens (see `WatchPanel.jsx`'s
 * `TickingCountdown`).
 */
export function countdownFor(subscription, nowMs) {
  if (isStale(subscription, nowMs)) return null
  const airsAt = subscription?.next_up?.airs_at
  const airsAtMs = instantOf(airsAt)
  if (airsAtMs === null) return null
  if (isExactCountdown(subscription)) {
    const seconds = Math.round((airsAtMs - nowMs) / 1000)
    return { grain: 'exact', text: exactCountdownText(seconds), seconds }
  }
  return { grain: 'day', text: dayGrainCountdownText(airsAtMs, nowMs) }
}

/** Is `next_up` scheduled for today, Eastern calendar day? Handoff's pulsing
 * ember dot and the guide's ember `today` label both read this. */
export function isTonight(subscription, nowMs) {
  const airsAtMs = instantOf(subscription?.next_up?.airs_at)
  if (airsAtMs === null) return false
  return easternDateKey(airsAtMs) === easternDateKey(nowMs)
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8b, manager review round 2 — THE ROW'S "FILM" LINE: `<runtime> ·
// released <year>`. `runtime_minutes` is `utils/watch_projection.py`'s own
// new field (`_runtime_minutes`) — a plain number, published unconditionally
// of `kind` (that module's risk item 2: presentation is the client's job).
// This is that presentation: the ONE place this repo decides the Film
// variant is what reads it, and the ONE place a minute count becomes text.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `2h 17m` / `1h` / `45m`, from a runtime in minutes — the design's own
 * fixtures (`D2D Watch v2.dc.html`: `'2h 17m'`, `'1h 43m'`, `'1h 34m'`).
 * `null` for anything that is not a positive number, so the Film line can
 * omit it rather than print a placeholder (manager review round 2,
 * verbatim: "omit missing parts, never a placeholder").
 *
 * Rounds to the nearest whole minute. The store can hold a fractional value
 * (`utils/watch_manager.py::_validate_runtime` accepts one — "not every
 * provider gives a whole-minute runtime") but no design state ever shows
 * sub-minute precision, so rounding for display is this function's one
 * presentation decision, never a fabricated exactness.
 */
export function movieRuntimeText(minutes) {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return null
  const total = Math.round(minutes)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

// ═══════════════════════════════════════════════════════════════════════════
// EASTERN CALENDAR HELPERS — one clock, shared by the countdown and the guide
// ═══════════════════════════════════════════════════════════════════════════

const EASTERN = 'America/New_York'

/** `YYYY-MM-DD` in Eastern time — `en-CA`'s locale format is that shape
 * natively, so no manual zero-padding or field reassembly is needed. */
const DATE_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: EASTERN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const WEEKDAY_SHORT = new Intl.DateTimeFormat('en-US', { timeZone: EASTERN, weekday: 'short' })
const SHORT_DATE = new Intl.DateTimeFormat('en-GB', { timeZone: EASTERN, day: 'numeric', month: 'short' })
const WEEKDAY_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: EASTERN,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})
const LONG_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: EASTERN,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})
/** V2-2b's own addition: `Sep 2026` — the design's own review-row date
 * (`v.when` in the prototype, e.g. "Aug 2026"), Eastern time like every
 * other date this file formats. */
const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { timeZone: EASTERN, month: 'short', year: 'numeric' })
const CLOCK_ET = new Intl.DateTimeFormat('en-GB', {
  timeZone: EASTERN,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function easternDateKey(ms) {
  return DATE_KEY.format(new Date(ms))
}

function easternWeekdayShort(ms) {
  return WEEKDAY_SHORT.format(new Date(ms))
}

/**
 * All of watch's short dates render in Eastern time (WT5's own timezone —
 * the group is near Atlanta, and the guide's day rows are Eastern calendar
 * days), never UTC. `freshness.js::shortDate` is fixed to UTC on purpose (it
 * dates a movie-night record against the bot's own wall clock), so this is a
 * deliberately separate, independently-timezoned formatter rather than a
 * shared one bent sideways for a second meaning.
 *
 * ⚠️ NONE OF THESE MANUALLY UPPERCASE THEIR OUTPUT. Every caller renders them
 * inside a `.d2d-micro` element, whose stylesheet rule already applies
 * `text-transform: uppercase` — the same convention `freshness.js`'s own
 * `label` and every existing panel's as-of stamp already rely on. Baking the
 * case into the string here would fight the same visual result while making
 * the underlying text (what a screen reader announces) needlessly shouted.
 */

/** `13 Sep`, in Eastern time. */
export function shortEasternDate(ms) {
  return SHORT_DATE.format(new Date(ms))
}

/** `Tue 22 Sep`, in Eastern time — the next-up date line (Handoff: "S4E8 ·
 * Tue 22 Sep"). */
export function weekdayEasternDate(ms) {
  return WEEKDAY_DATE.format(new Date(ms))
}

/** `18 Apr 2025`, in Eastern time — a film's release date (Handoff:
 * "RELEASED 18 APR 2025"). */
export function longEasternDate(ms) {
  return LONG_DATE.format(new Date(ms))
}

/** `17:00`, in Eastern time — the guide's exact-time rendering. */
export function easternClock(ms) {
  return CLOCK_ET.format(new Date(ms))
}

/** `Sep 2026`, in Eastern time — a review's own `updated_at`, formatted the
 * way the title page's reviews section shows it beside the score. V2-2b. */
export function monthYearLabel(ms) {
  return MONTH_YEAR.format(new Date(ms))
}

/** Whole calendar days between two `YYYY-MM-DD` Eastern date keys. Both keys
 * come from `DATE_KEY`, so this is integer day arithmetic on `Date.UTC`
 * midnights and never touches a real timezone offset a second time. */
function easternDaysBetween(fromKey, toKey) {
  const [fy, fm, fd] = fromKey.split('-').map(Number)
  const [ty, tm, td] = toKey.split('-').map(Number)
  const from = Date.UTC(fy, fm - 1, fd)
  const to = Date.UTC(ty, tm - 1, td)
  return Math.round((to - from) / DAY_MS)
}

// ═══════════════════════════════════════════════════════════════════════════
// POSTERS AND PROVIDER LOGOS — already-absolute URLs, two possible hosts
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 UNLIKE `movie.js::posterUrl`, THIS FILE DOES NOT PICK A HOST OR A SIZE.
// `utils/watch_manager.py`'s own docstring (W0-review M6) is explicit:
// `poster_url` "has always been a ready-to-use absolute URL... never a
// TMDB-relative path," resolved once from `SearchHit.poster_url` for BOTH
// providers. W2a's own example payload shows it: a TV/movie poster from
// `image.tmdb.org`, an anime poster from `s4.anilist.co` — two real hosts,
// neither of them `movie.js`'s single `POSTER_HOST`. The same is true of a
// structured provider chip's `logo_url` (W2a2): TMDB builds it server-side at
// a fixed size (`w92`), so there is no size ladder to choose here either.
//
// ✅ SO THIS FOLLOWS `music.js::artworkUrl`'s SHAPE, NOT `movie.js`'s: accept
// any well-formed `https:` URL and refuse everything else, rather than
// allow-listing a host. An allow-list of exactly two hosts would be a source
// coupling wearing a security control's clothes — music.js's own words for the
// identical judgment call, and it transfers unchanged: TMDB and AniList are
// the only two providers in v1 (WT4), and a hardcoded pair is wrong the day a
// third one is added, the same way an image host allow-list would be for
// music's extractors.
//
// ⚠️ `http:` IS REFUSED. A ready-to-use URL rendered on a page served over
// HTTPS is mixed content the browser blocks before a byte moves; refusing it
// here means the fallback (`onError`) fires from an image that never even
// attempted the request, rather than from a real blocked one — the same
// outcome, reached honestly.

/** An already-absolute poster or provider-logo URL, or `null`. */
export function imageUrl(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  let parsed
  try {
    // No base: a scheme-relative `//host/x.jpg` throws rather than inheriting
    // the page's own origin — `music.js::artworkUrl`'s exact reasoning.
    parsed = new URL(raw)
  } catch {
    return null
  }
  return parsed.protocol === 'https:' ? raw : null
}

// ═══════════════════════════════════════════════════════════════════════════
// THE WEEK GUIDE — bucketed, never re-bounded (the server already did that)
// ═══════════════════════════════════════════════════════════════════════════

/** Sunday through Saturday, the guide's fixed row order (Handoff: "seven
 * rows, Sunday to Saturday"). */
export const WEEKDAY_ORDER = Object.freeze(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])

/**
 * Seven day-buckets for the guide, each `{ day, isToday, entries }`.
 *
 * `utils/watch_projection.py::_airing_this_week` already bounds `entries` to
 * the Sunday–Saturday Eastern week containing the projection's own `as_of` —
 * this function does not recompute that boundary (a second implementation of
 * one derivation is exactly what CLAUDE.md gotcha 1's *copies* axis warns
 * against); it only buckets the entries it was handed by their OWN Eastern
 * weekday, and separately marks which bucket is `nowMs`'s Eastern today.
 *
 * Each entry is joined against `subscriptions[]` by `subscription_id` so the
 * row has a title, a kind and a poster to draw — an entry whose subscription
 * cannot be found (should not happen; the projection is built from the same
 * store both ways) is dropped rather than rendered with a blank title.
 */
export function weekBuckets(shared, nowMs) {
  const todayKey = easternDateKey(nowMs)
  const subsById = new Map(subscriptionsOf(shared).map((sub) => [sub.id, sub]))
  const buckets = WEEKDAY_ORDER.map((day) => ({ day, isToday: day === easternWeekdayShort(nowMs), entries: [] }))
  const indexOf = new Map(WEEKDAY_ORDER.map((day, index) => [day, index]))

  for (const entry of airingThisWeekOf(shared)) {
    const ms = instantOf(entry.airs_at)
    if (ms === null) continue
    const sub = subsById.get(entry.subscription_id)
    if (!sub) continue
    const index = indexOf.get(easternWeekdayShort(ms))
    if (index === undefined) continue
    buckets[index].entries.push({
      subscriptionId: entry.subscription_id,
      season: entry.season,
      episode: entry.episode,
      airsAtMs: ms,
      isToday: easternDateKey(ms) === todayKey,
      title: sub.title,
      kind: sub.kind,
      posterUrl: sub.poster_url,
      isExact: isExactCountdown(sub),
      isStale: isStale(sub, nowMs),
    })
  }
  return buckets
}

/** Does the guide have anything in it at all? Handoff state 14: "nothing
 * airs" is drawn only when every one of the seven rows is genuinely empty. */
export function weekIsEmpty(buckets) {
  return buckets.every((bucket) => bucket.entries.length === 0)
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-6B — THE THIRD LIST TAB: WATCHING · AWAITING NEXT SEASON · FINISHED
// ═══════════════════════════════════════════════════════════════════════════
//
// `docs/briefs/V2-6b-awaiting-tab.md` §2, RECONCILIATION.md item 12, spec §5.
// Every subscription this file's callers ever iterate over is already
// `status !== 'dropped'` filtered by the CALLER — Dropped stays its own
// section "whatever else is true" (RECONCILIATION item 10/12) and is never
// one of this section's three tab values.
//
// 🔴 P6-299b — THIS SECTION USED TO RE-DERIVE THE TIE-BREAK BELOW; IT NOW
// READS THE BOT'S OWN ANSWER. `docs/briefs/P6-299b-site-reads-the-shelf.md`.
// Until this slice, `isAwaitingNextSeason` computed "is this title on
// Awaiting" itself, from `awaiting`/`next_up`/`showStanding` — a second
// implementation of a rule `utils/watch_projection.py::compute_shelf` (the
// bot) ALSO has to get right, and the two silently disagreed in real cases
// (`docs/reports/P6-299a.md` §3): a crew-finished show whose sequel starts
// airing stayed "Finished" here while the bot already knew better; a title
// nobody current was watching, which a member had finished without anyone
// running `/watch finish`, stayed "Watching"; a TMDB show between seasons
// with a DATED premiere stayed "Watching" too, because a real `next_up`
// short-circuited this file's own rule before it ever asked what that
// `next_up` was the premiere OF. Gotcha 21's own shape: a guard and the
// thing it guards computing the same fact twice is two chances to disagree,
// not one. `compute_shelf` now publishes `shelf` on every subscription
// (P6-299a); this file reads it, full stop — see `titleShelf`, below.
//
// The tie-break paragraph that used to live here is kept, for HISTORY rather
// than as a description of current behaviour (a superseded design is worth
// more as a record of why the bot's rule exists than as a silently deleted
// diff — this file's own convention, matching CLAUDE.md's for the doc it
// lives beside): spec §5 reads "the title moves to Awaiting, carrying the
// sequel" literally -- V2-6a's own `awaiting` field is set on a title that is
// STILL AIRING the moment its sequel is linked. The design's own sample
// (`apo2`: airing, Season 3 announced) keeps that title in WATCHING, with the
// announcement as a note -- and Jordan's own brief broke the tie: "'watching'
// (content available now)' or awaiting." `compute_shelf` (bot-side) now
// enforces exactly this ordering — row 3 (current season airing) is checked
// before row 4 (more is coming) — so a title moves to Awaiting only when its
// OWN season has nothing left airing, never merely because a sequel or a
// season has been announced while episodes are still coming.

//: The four values `compute_shelf` (bot, `utils/watch_projection.py`)
//: publishes on every subscription's own `shelf` field, named here once —
//: matching the bot's own `SHELF_*` names — rather than typed as bare
//: strings at every comparison in this file and its callers (`WatchPanel.jsx`,
//: `WatchTitle.jsx`).
export const SHELF_WATCHING = 'watching'
export const SHELF_AWAITING = 'awaiting'
export const SHELF_FINISHED = 'finished'
export const SHELF_DROPPED = 'dropped'

//: `_member_standing`'s own sibling four, one per `members[]` entry's own
//: `standing` (P6-299a §2.3) — a DIFFERENT vocabulary from the shelf's on
//: purpose (a title's shelf and a member's standing answer different
//: questions; see `cogs/watch/helpers.py::shelf_label`'s own docstring,
//: bot-side, for the same warning about a THIRD, unrelated "shelf").
export const STANDING_WATCHING = 'watching'
export const STANDING_CAUGHT_UP = 'caught_up'
export const STANDING_FINISHED = 'finished'
export const STANDING_STOPPED = 'stopped'

const RECOGNISED_SHELVES = new Set([SHELF_WATCHING, SHELF_AWAITING, SHELF_FINISHED, SHELF_DROPPED])

/**
 * The published `shelf`, defended against the one real gap the brief names:
 * a site deploy can land before the bot's first publish of the field (or,
 * symmetrically, a future bot value this build predates). Either way this
 * reads as `watching` -- never a crash, and never a title silently vanishing
 * from every tab (a title this function cannot place is still ON the
 * watchlist, and `watching` is the least surprising of the three list tabs
 * to find it under) -- for however many minutes the gap lasts. Every reader
 * of a subscription's shelf in this file (`watchListTab`, `serverStanding`,
 * `isAwaitingNextSeason`) goes through this one function, so the fallback is
 * decided once, not re-invented per caller (never keep two answers).
 *
 * Never re-derived beyond this: the bot is the one and only place a shelf is
 * COMPUTED (gotcha 21) -- this function only defends against the field being
 * absent or unrecognised; it does not stand in for `compute_shelf` when the
 * field IS present and recognised.
 */
export function titleShelf(subscription) {
  const shelf = subscription?.shelf
  return RECOGNISED_SHELVES.has(shelf) ? shelf : SHELF_WATCHING
}

/**
 * `_awaiting`'s own "third shape" (`utils/watch_projection.py` case 3) --
 * a title that has genuinely never aired, whether or not a premiere date
 * is known.
 *
 * 🔴 P6-299b: no longer part of the Awaiting DECISION -- `isAwaitingNextSeason`
 * (below) reads the published `shelf` for that now. Kept for the Awaiting
 * row's own TEXT, which still needs to tell "never aired" apart from
 * "between seasons" to choose between "Not out yet" and "Between seasons"
 * (`awaitingBaseText`, its only remaining caller) -- the brief's own words:
 * "read the published `awaiting` FACT, not the shelf, and still decide the
 * words." One derivation, never two ways of noticing the identical shape
 * (gotcha 1's *copies* axis). `next_id`/`season_number` are only ever
 * populated TOGETHER with `awaiting` itself being non-`null` (`_awaiting`'s
 * own three-case contract), so `awaiting != null` here is never true for any
 * OTHER reason than this one shape.
 */
export function neverAiredAtAll(subscription) {
  const awaiting = subscription?.awaiting ?? null
  return awaiting != null && awaiting.next_id == null && awaiting.season_number == null
}

/**
 * Whether a listed, non-dropped subscription belongs on `Awaiting next
 * season` rather than `Watching`/`Finished`.
 *
 * 🔴 P6-299b: reads the published `shelf` (`titleShelf`, above) -- full stop.
 * This used to re-derive the answer from `awaiting`/`next_up`/`showStanding`
 * (see the section header above for that rule's own history and why it
 * drifted from the bot's); every caller now gets the SAME answer
 * `compute_shelf` (bot-side) computed, never a second opinion. Still reads
 * NOTHING about `members`/`progress` -- the shelves are per TITLE, never per
 * viewer (RECONCILIATION item 12: Severance sits on Awaiting while one
 * member is five episodes behind the group) -- `compute_shelf` itself never
 * reads a member's own progress either, for the identical reason.
 */
export function isAwaitingNextSeason(subscription) {
  return titleShelf(subscription) === SHELF_AWAITING
}

/**
 * Which of the three list tabs a subscription belongs in — never Dropped,
 * which is its own section regardless (the caller filters it out before
 * this is ever asked; see `subscriptionsByListTab` below, the one place
 * that contract is enforced). Every listed, non-dropped subscription is in
 * EXACTLY one of the three (the brief's own rule): Awaiting is carved out
 * of BOTH the `watching` and `finished` pools before either bucket is
 * read, never a fourth state layered on top of `status`.
 *
 * 🔴 P6-299b: the published `shelf`, verbatim (`titleShelf`, above) -- this
 * used to be a three-way `isAwaitingNextSeason` / `status === 'finished'` /
 * fallback join; it is now a direct read of the one fact the bot already
 * computed, so the tabs and `serverStanding`'s own chip can never disagree
 * (brief §2.1: "a title's chip always matches the tab it sits in"). A
 * dropped subscription's own `shelf` is always `dropped` (`compute_shelf`
 * checks it first, before anything else), so this can return `dropped` too
 * when called directly on one -- harmless, since `subscriptionsByListTab`
 * below never asks it for a tab `dropped` could match, and no other caller
 * in this repo reaches this function with a dropped subscription at all.
 */
export function watchListTab(subscription) {
  return titleShelf(subscription)
}

/**
 * Subscriptions in one of the three list tabs — reads `watchListTab`,
 * never re-derives it, so the tab counts and the tab's own row list can
 * never disagree (the brief's own words: "one pure helper... and the
 * counts and the lists both read it"). Dropped subscriptions are excluded
 * outright, matching `subscriptionsByStatus(shared, 'dropped')`'s own
 * separate, unconditional section (RECONCILIATION item 10/12) — this
 * function is never how a caller reaches a dropped title.
 */
export function subscriptionsByListTab(shared, tab) {
  return subscriptionsOf(shared).filter((sub) => sub.status !== 'dropped' && watchListTab(sub) === tab)
}

// ═══════════════════════════════════════════════════════════════════════════
// P6-298b — A SERIES: the bot's own `series` map (`utils/watch_projection.py::
// compute_watch_series`, P6-298a), read verbatim. Gotcha 21: never re-derive a
// face, a shelf, or a chain — every function below only reads what the bot
// already decided, never re-walks `franchise.next`/`previous` itself.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * One series' own entry — `{face_id, shelf, seasons}` — or `null` when
 * `seriesId` is not a real key into `shared.series`. Covers every reason it
 * could be missing the same way: `seriesId` itself absent/not a string, the
 * whole `series` map absent (the minutes between a site deploy and the bot's
 * first publish of this field — P6-298a's own brief), or an id the map
 * simply does not carry an entry for. Every caller in this file treats `null`
 * as "no series data yet, render as if the feature did not exist" — never a
 * crash, per gotcha 21's sibling rule that a guard must degrade honestly.
 */
export function seriesEntryFor(shared, seriesId) {
  if (typeof seriesId !== 'string' || seriesId === '') return null
  const map = shared?.series
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null
  const entry = map[seriesId]
  return entry && typeof entry === 'object' && !Array.isArray(entry) ? entry : null
}

/** `series.seasons`, defensively — `[]` for anything else. Already root-to-
 * tail, listed or not (`compute_watch_series`'s own order) — never re-sorted
 * here. */
export function seriesSeasonsOf(series) {
  const list = series?.seasons
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/**
 * One row per series, for a Watching/Awaiting/Finished list tab — P6-298b
 * §2.1. Placed by the SERIES' own published `shelf`, never the face's own
 * individual shelf (`docs/reports/P6-298a.md` §3 documents the case where the
 * two genuinely disagree: a dropped face under a series whose own shelf is
 * `finished`). A listed season that is not its series' face never gets its
 * own row — only the face's own row is returned, carrying `seasonCount` for
 * the "N seasons" badge (`Row`, `WatchPanel.jsx`).
 *
 * A subscription whose `series_id` cannot be resolved against `shared.series`
 * falls back to being its own series of one, exactly the pre-P6-298b
 * behaviour: bucketed by its OWN `titleShelf`, `seasonCount: 1` (no badge).
 * Every subscription is judged independently, so a deploy window where only
 * SOME rows carry a resolvable `series_id` degrades row by row, never
 * all-or-nothing.
 *
 * The tab's own COUNT is this array's `.length` — `Tabs` and the row list
 * (`WatchPanel.jsx`) both call this SAME function, so the two can never
 * disagree, the identical discipline `subscriptionsByListTab` already
 * established for the un-grouped tabs.
 */
export function seriesRowsByListTab(shared, tab) {
  const subs = subscriptionsOf(shared)
  const byId = new Map(subs.map((sub) => [sub.id, sub]))
  const seen = new Set()
  const rows = []
  for (const sub of subs) {
    // Dropped stays its own section regardless of series (RECONCILIATION
    // item 10/12), matching `subscriptionsByListTab`'s own pre-filter — never
    // reachable as one of THIS function's three tab values. A series with a
    // non-dropped shelf always has at least one non-dropped member (a
    // dropped `status` always publishes a dropped `shelf` — `compute_shelf`
    // rule 1), so skipping a dropped member here as an entry point never
    // hides a series; it is still discovered via that other member, and its
    // FACE (looked up in `byId`, built from every subscription including
    // dropped ones) still resolves correctly even when the face itself is
    // individually dropped.
    if (sub.status === 'dropped') continue
    const series = seriesEntryFor(shared, sub.series_id)
    const groupKey = series ? sub.series_id : `solo:${sub.id}`
    if (seen.has(groupKey)) continue
    seen.add(groupKey)
    const placement = series && RECOGNISED_SHELVES.has(series.shelf) ? series.shelf : titleShelf(sub)
    if (placement !== tab) continue
    const seasons = seriesSeasonsOf(series)
    const face = series ? (byId.get(series.face_id) ?? sub) : sub
    rows.push({ subscription: face, seasonCount: series ? seasons.length : 1 })
  }
  return rows
}

/** A single word, stripped of leading/trailing punctuation and lower-cased —
 * comparison-only normalisation for `seriesSeasonLabel`, below. The ORIGINAL
 * word (not this one) is what survives into a label's remainder, so a colon
 * glued to a shared word (`"Revengers:"`) is discarded whole along with the
 * word it matched, never left dangling. */
function normalizedWord(word) {
  return word.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')
}

/**
 * A season's own tab/archive label — P6-298b §2.2/§2.3: its full title with
 * the series' common leading words removed, falling back to the FULL title
 * when nothing meaningful is left. The brief's own examples, all produced by
 * this function against a "Tokyo Revengers" reference: `"Season 2"`,
 * `"Season 2 Part 2"`, `"Santen Sensou-hen"`.
 *
 * `referenceTitle` is the series' own fixed reference point — by convention,
 * the chain's FIRST (root) season's title (`seriesSeasonsOf(series)[0]
 * .title`; the archive's own caller uses the equivalent root row, see
 * `watchArchive.js::seriesDisplayName`). Comparing every season against ONE
 * fixed reference, rather than requiring the whole chain to agree on a
 * single global prefix, is what "keeps its own title" for a movie or a
 * special threaded into the chain (brief §2.2): if its title does not start
 * with the reference's words at all, zero words match and the title comes
 * back completely unchanged. This needs no `kind` check at all — which
 * matters because the series map publishes no `kind` for a non-face season
 * in the first place — it is an emergent property of comparing prefixes,
 * never a re-derivation of which entry "is" a movie.
 *
 * Matching is by WHITESPACE-SPLIT WORD, case-insensitively, ignoring each
 * word's own leading/trailing punctuation (`normalizedWord`) — so
 * `"Revengers:"` still matches `"Revengers"` — but the words consumed as
 * "common" are discarded WHOLE, so a colon glued to the last shared word
 * never survives into the remainder. Any leading punctuation/whitespace left
 * over on the remainder (a colon or dash that was its OWN token) is trimmed
 * too.
 */
export function seriesSeasonLabel(title, referenceTitle) {
  const full = typeof title === 'string' ? title : ''
  const reference = typeof referenceTitle === 'string' ? referenceTitle : ''
  const words = full.trim().split(/\s+/).filter((word) => word !== '')
  const referenceWords = reference.trim().split(/\s+/).filter((word) => word !== '')

  let commonCount = 0
  while (
    commonCount < words.length &&
    commonCount < referenceWords.length &&
    normalizedWord(words[commonCount]) !== '' &&
    normalizedWord(words[commonCount]) === normalizedWord(referenceWords[commonCount])
  ) {
    commonCount += 1
  }

  const remainder = words
    .slice(commonCount)
    .join(' ')
    .replace(/^[\s:,\-–—]+/, '')
    .trim()
  return remainder !== '' ? remainder : full
}

/**
 * View-models for the title page's season tab strip — P6-298b §2.2. `[]`
 * ("a series of one shows no strip") when the series has one season or
 * fewer, INCLUDING when there is no series data at all yet (the same
 * deploy-gap fallback `seriesRowsByListTab` applies: no data reads as if the
 * feature did not exist, never a crash). Seasons stay in the bot's own
 * root-to-tail chain order — never re-sorted.
 */
export function seriesTabsFor(series, currentSeasonId) {
  const seasons = seriesSeasonsOf(series)
  if (seasons.length <= 1) return []
  const referenceTitle = typeof seasons[0]?.title === 'string' ? seasons[0].title : ''
  return seasons.map((season) => ({
    id: season.id,
    title: typeof season.title === 'string' ? season.title : '',
    posterUrl: typeof season.poster_url === 'string' ? season.poster_url : null,
    label: seriesSeasonLabel(season.title, referenceTitle),
    isCurrent: season.id === currentSeasonId,
  }))
}

/**
 * The Awaiting row's own base text — brief §3: "a sequel names the
 * sequel's title... a scheduled season names its number... otherwise one
 * plain line." Reads ONLY `awaiting`'s own shape plus `neverAiredAtAll`
 * (never re-derives WHICH case applies beyond that, which is
 * `isAwaitingNextSeason`'s job). A subscription this is called on is
 * assumed already classified onto the Awaiting shelf (or, on the title
 * page, already known to carry an announced sequel/season — see
 * `WatchTitle.jsx::FranchiseNote`, which never reaches the two lines below
 * since its own gate excludes `neverAiredAtAll` by construction).
 *
 * 🔴 **Round 2 (manager ruling): a title that has never aired is NOT
 * "between seasons."** The first cut used one generic fallback line for
 * BOTH `neverAiredAtAll` and the `showStanding`-only case, on the theory
 * that the brief's own test list names only three text variants ("sequel,
 * season, and bare"). Told straight: that read "bare" as one shape when
 * the site can already tell the two apart by name
 * (`neverAiredAtAll`, above) — so the fallback was claiming something the
 * data contradicts. Two distinct lines now:
 * - **`neverAiredAtAll`** → **"Not out yet"** — nothing has aired, whether
 *   or not a date is on file.
 * - **Otherwise** (no `awaiting` fact at all; the between-seasons signal
 *   came from `showStanding` alone) → **"Between seasons"**, the design's
 *   own example text, kept for the case it actually describes: a title
 *   with a real prior season and nothing scheduled yet.
 */
export function awaitingBaseText(subscription) {
  const awaiting = subscription?.awaiting ?? null
  if (awaiting?.next_id != null && typeof awaiting.next_title === 'string' && awaiting.next_title) {
    return awaiting.next_title
  }
  if (awaiting?.season_number != null) return `Season ${awaiting.season_number}`
  if (neverAiredAtAll(subscription)) return 'Not out yet'
  return 'Between seasons'
}

/**
 * 🔴 Round 2 (manager ruling): whether an already-resolved instant has
 * passed as of `nowMs` — the same "safety net" shape
 * `cogs/watch/helpers.py::_availability_phrase` (V2-6a round 3) already
 * applies to the bot's own announcement line, needed here for the
 * identical reason. `utils/watch_projection.py::_sequel_awaits` treats
 * AniList's `NOT_YET_RELEASED` as awaiting REGARDLESS of the date on file
 * (its own docstring: AniList's status can lag a real premiere), so
 * `awaiting.next_air` can genuinely be a PAST instant while `awaiting`
 * stays populated. A caller that assumes "dated" means "still ahead of
 * now" reproduces V2-6a round 3's own bug (a future-tense phrase, or a
 * countdown, for something that has already happened).
 */
export function isPastInstant(airsAtMs, nowMs) {
  return airsAtMs !== null && airsAtMs <= nowMs
}

/**
 * `countdownFor`'s IDENTICAL grain/formatting rules (`isExactCountdown`,
 * `exactCountdownText`, `dayGrainCountdownText`), reused rather than
 * edited — `countdownFor` itself reads `subscription.next_up.airs_at`, and
 * is one of V2-8b's own countdown rules this slice's brief says must not
 * change. The Awaiting Next-line needs the identical grain/format for a
 * DIFFERENT instant (`awaiting.next_air`, a linked sequel's or a pending
 * season's own date — never `next_up`, which is `null` whenever this is
 * relevant). Staleness is the CALLER's job here (`RowNextAwaiting`,
 * `WatchPanel.jsx`) — checked once for the whole line rather than repeated
 * by this function, the same split `countdownFor`/`isStale` already have.
 *
 * 🔴 Round 2: NEVER a countdown to a PAST instant, whatever the grain —
 * `isPastInstant` (above), checked before either grain's own formatter
 * runs. `exactCountdownText` would otherwise clamp a negative duration to
 * an honest-looking `"0s"`, and `dayGrainCountdownText` reads ANY negative
 * day count as `"today"` — both plausible-looking and both wrong for a
 * date that has already passed, which `awaiting.next_air` genuinely can be
 * (see `isPastInstant`'s own docstring).
 */
export function countdownForInstant(subscription, airsAtMs, nowMs) {
  if (airsAtMs === null || isPastInstant(airsAtMs, nowMs)) return null
  if (isExactCountdown(subscription)) {
    const seconds = Math.round((airsAtMs - nowMs) / 1000)
    return { grain: 'exact', text: exactCountdownText(seconds), seconds }
  }
  return { grain: 'day', text: dayGrainCountdownText(airsAtMs, nowMs) }
}

/**
 * Spec §5, verbatim: "no next season found — link one if you know it,"
 * shown on a FINISHED title's own page once nothing at all is known about
 * what comes next. "Known" means a franchise link OR a TMDB pending season
 * — a linked sequel that is already available (RELEASING/FINISHED, so
 * `awaiting` reads `null` for it) still counts as known; only a title with
 * NEITHER a link NOR a pending season reads "not found". Never shown for a
 * title THIS slice has already pulled onto the Awaiting shelf — that
 * title's own row (`RowNextAwaiting`) already names what is coming, and
 * the title page gets `FranchiseNote`'s "announced" branch instead (see
 * that component).
 */
export function noNextSeasonFound(subscription) {
  return (
    subscription?.status === 'finished' &&
    !isAwaitingNextSeason(subscription) &&
    (subscription?.franchise?.next ?? null) === null &&
    (subscription?.awaiting ?? null) === null
  )
}
